import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import BreadCrumb from "../components/breadCrumb";
import DataGrid from "../components/common/DataGrid";
import { NumericBox } from "../components/common/NumericBox";
import { SelectBox } from "../components/common/SelectBox";
import { TextArea } from "../components/common/TextArea";
import { TextBox } from "../components/common/TextBox";
import PageContent from "../components/pageContent";
import PopupContent from "../components/popupContent";
import { FormatString } from "../helpers/stringHelpers";
import TranslationContext from "../providers/TranslationContext";
import { ValidationProvider } from "../providers/ValidationContext";
import { addNotification } from "../redux/actions";
import {
  apiCreateOrUpdatePigment,
  apiGetAllPigments,
  apiGetPigmentDetail,
} from "../services/PigmentsService";
import { validateForm } from "../validation/formValidation";
import { model } from "../validation/models/pigment";
import PopupOperationsBar from "./PopupOperationsBar";
import CheckBox from "../components/common/CheckBox";
import Button from "../components/common/Button";
import ComboBoxData from "../components/common/ComboBoxData";

// styles
require("./PopupGeneralStyles.css");

interface Props {
  id?: number;
  handleCancel: any;
  refreshData: any;
  discountGroups: any[];
  pigments: any;
}

const PigmentyPopup = (props: Props) => {
  const translationContext = useContext(TranslationContext) as any;
  const prefix = translationContext.prefix;
  const { t } = useTranslation(undefined, { keyPrefix: `${prefix}.Popup` });
  const cloneDeep = require('clone-deep');
  const [validationModel, setValidationModel] = useState(cloneDeep(model));

  const [loading, setLoading] = useState(true);
  const data: any = {};
  const [formData, setFormData] = useState(data);
  const [saveError, setSaveError] = useState(false);

  const isEditMode = props.id ? true : false;

  const pigmentsForSorting = props.id ? props.pigments.filter(a => a.id != props.id) : null;
  const defaultPigmentForSorting = pigmentsForSorting ? pigmentsForSorting[0].id : 0;
  const [sortOrderOptions, setSortOrderOptions] = useState({ 'changeSortOrder': false, 'direction': 'before', 'pigmentId': defaultPigmentForSorting })


  const dispatch = useDispatch();

  useEffect(() => {
    if (props.id) {
      apiGetPigmentDetail(props.id).then((responseData) => {
        setFormData(responseData.data);

        setLoading(false);
      }).catch((err: any) => {
        dispatch(addNotification({ text: FormatString(t('LoadingDetailsError'), `${props.id}`), type: 'error' }));
        props.handleCancel();
      });
    } else {
      setLoading(false);
    }
  }, []);

  const onAddingClick = () => {
    validateForm(validationModel, formData, isEditMode);
    setValidationModel({ ...validationModel });
    if (validationModel.isValid) {
      setLoading(true);
      apiCreateOrUpdatePigment(formData)
        .then((responseData) => {
          dispatch(addNotification({ text: FormatString(t('CreateSuccess'), formData.code), type: 'success' }));
          props.refreshData();
          props.handleCancel();

          setLoading(false);
        })
        .catch((err: any) => {
          dispatch(addNotification({ text: FormatString(t('CreateFailure'), formData.code), type: 'error' }));
          setSaveError(true);
        });
    }
  };

  const onEditingClick = () => {
    validateForm(validationModel, formData, isEditMode);
    setValidationModel({ ...validationModel });
    if (validationModel.isValid) {
      setLoading(true);
      apiCreateOrUpdatePigment({
        ...formData,
        UpdateSortOrder: sortOrderOptions.changeSortOrder,
        SortOrderDirectionIsBefore: sortOrderOptions.direction == "before",
        SortOrderPigmentId: sortOrderOptions.pigmentId
      })
        .then((responseData) => {
          dispatch(addNotification({ text: FormatString(t('EditSuccess'), formData.code), type: 'success' }));
          props.refreshData();
          props.handleCancel();

          setLoading(false);
        })
        .catch((err: any) => {
          dispatch(addNotification({ text: FormatString(t('EditFailure'), formData.code), type: 'error' }));
          setSaveError(true);
        });
    }
  };

  const onCancelClick = () => {
    props.handleCancel();
  };


  const handlePigmentChange = (value: number) => {
    setSortOrderOptions({ ...sortOrderOptions, pigmentId: value });
  };

  return (
    <PopupContent header={props.id ? t('EditLabel') : t('CreateLabel')}>
      <ValidationProvider value={validationModel}>
        <TextBox
          dataField="name"
          defaultValue={formData.name}
          formData={formData}
          setStateMethod={setFormData}
          id="name"
          label={t('Labels.name')}
          placeholder={t('Labels.namePlaceholder')}
        />
        <TextBox
          dataField="code"
          defaultValue={formData.code}
          formData={formData}
          setStateMethod={setFormData}
          id="code"
          label={t('Labels.code')}
          placeholder={t('Labels.codePlaceholder')}
        />
        <NumericBox
          dataField="price"
          defaultValue={formData.price}
          formData={formData}
          setStateMethod={setFormData}
          id="price"
          label={t('Labels.price')}
        />
        {
          /*
          <NumericBox
            dataField="margin"
            defaultValue={formData.margin}
            formData={formData}
            setStateMethod={setFormData}
            id="margin"
            label={t('Labels.margin')}
          />
          */
        }
        <NumericBox
          dataField="density"
          defaultValue={formData.density}
          formData={formData}
          setStateMethod={setFormData}
          id="density"
          label={t('Labels.density')}
          decimalPlaces={4}
        />
        <SelectBox
          dataField="discountGroupId"
          value={formData.discountGroupId}
          formData={formData}
          label={t('Labels.discountGroup')}
          options={props.discountGroups}
          placeholder={t('Labels.discountGroupPlaceholder')}
          setStateMethod={setFormData}
          isDisabled={false}
        />
        <TextArea
          id="noteInternal"
          label={t('Labels.note')}
          placeholder={t('Labels.noteGroupPlaceholder')}
          rows={4}
          dataField="noteInternal"
          value={formData.noteInternal}
          formData={formData}
          setStateMethod={setFormData}
        />

        {props.id && <>
          <CheckBox
            id="changeSortOrder"
            label={`${t('Labels.changeSortOrder')}`}
            paddingLeft={"10px"}
            handleCheckedChange={(checked) => {

              setSortOrderOptions({ ...sortOrderOptions, changeSortOrder: checked });
            }}
            defaultValue={false}
          />

          {sortOrderOptions.changeSortOrder &&
            <div className="sortOrderOptions">
              <Button iconClass="" bold={sortOrderOptions.direction == 'before'} onClick={() => { setSortOrderOptions({ ...sortOrderOptions, direction: 'before' }) }} text={t('BeforePigment')} />
              <Button iconClass="" bold={sortOrderOptions.direction == 'after'} onClick={() => { setSortOrderOptions({ ...sortOrderOptions, direction: 'after' }) }} text={t('AfterPigment')} />

              <ComboBoxData
                id="pigments"
                label={t('Pigment')}
                dataTextField="name"
                dataValueField="id"
                defaultValue={defaultPigmentForSorting}
                handleChange={handlePigmentChange}
                rows={pigmentsForSorting}
              />
            </div>
          }

        </>}

        <PopupOperationsBar
          handleAdding={onAddingClick}
          handleCancellation={onCancelClick}
          handleEditing={onEditingClick}
          id={props.id}
        />
      </ValidationProvider>
    </PopupContent>
  );
};

export default PigmentyPopup;
